import moment from "moment";
import * as React from "react";
import { ComponentType } from "react";
import { connect } from "react-redux";
import { routerActions } from "react-router-redux";
import ReactTable, { RowInfo } from "react-table";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import { Link } from "src/components/routing";
import { PAGE_SIZE_OPTIONS } from "src/constants/reactTableConstants";
import { CommitteeMeetingsBreadcrumbs } from "src/helpers/breadcrumbHelper";
import RouterHoc, { IRouterActions, IRouterProps } from "src/hoc/RouterHoc";
import * as CommitteeMeetingActions from "../../../actions/committeeMeeting";
import * as CommitteeActions from "../../../actions/committees";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import { RestrictedLayout } from "../../../components/layouts/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { CommitteeSubMenu } from "../../../helpers/subMenuHelper";
import { IFormFilter, ITmdState } from "../../../reducers/index";

import "./CommitteeMeetingsPage.scss";

interface ICommitteeMeetingsPageParams {
    committeeId: number;
}

interface ICommitteeMeetingsPageProps
    extends IRouterProps<ICommitteeMeetingsPageParams> {
    committeeId: number;

    // From state
    formFilter: IFormFilter;
    committee: Dtos.Committee;
    committeeMeetings: Dtos.CommitteeMeeting[] | undefined;
    lookups: Dtos.Lookup[];

    loadingCommittee: boolean;
    loadCommitteeSuccess: boolean;
    loadCommitteeFailure: boolean;
    loadCommitteeErrorCode: string;

    loadingCommitteeMeetings: boolean;
    loadCommitteeMeetingsSuccess: boolean;
    loadCommitteeMeetingsFailure: boolean;
    loadCommitteeMeetingsErrorCode: string;

    user: Dtos.User;
    permissions?: Dtos.PersonnelTmdPermissions[];

    pageSize: number;
}

interface ICommitteeMeetingsPageActions extends IRouterActions {
    clearCommitteeMeetings: CommitteeMeetingActions.ICommitteeMeetingClearActionCreator;
    loadCommitteeMeetings: CommitteeMeetingActions.ICommitteeMeetingLoadByCommitteeIdActionCreator;

    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;

    navigate: typeof routerActions.push;
    navigateReplace: typeof routerActions.replace;

    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;

    clearCommittee: CommitteeActions.ICommitteeClearActionCreator;
    loadCommittee: CommitteeActions.ICommitteeLoadByIdActionCreator;

}

type CommitteeMeetingsPageProps = ICommitteeMeetingsPageProps &
    ICommitteeMeetingsPageActions;

class CommitteeMeetingsPage extends React.PureComponent<
    CommitteeMeetingsPageProps,
    any
> {
    initialised: boolean;

    constructor(props: CommitteeMeetingsPageProps) {
        super(props);
        this.initialised = false;
        this.clearCommittee = this.clearCommittee.bind(this);
        this.toggleExportMenu = this.toggleExportMenu.bind(this);

        this.state = {
            exportToggle: false,
        };
    }

    componentDidMount() {
        const {
            loadCommitteeMeetings,
            committeeId,
            permissions,
            setDrawerItems,
            setBreadcrumbs,
            loadCommittee
        } = this.props;
        

        if (committeeId) {
            setDrawerItems(CommitteeSubMenu(committeeId, permissions));
            setBreadcrumbs(CommitteeMeetingsBreadcrumbs(committeeId));
            loadCommittee(committeeId);
            loadCommitteeMeetings(committeeId);
        }
    }

    componentDidUpdate(prevProps: CommitteeMeetingsPageProps, _prevState) {
        const {
            committee,
            committeeId,
            loadCommittee,
            setDrawerItems,
            setBreadcrumbs,
            permissions,
            loadCommitteeFailure,
            loadCommitteeMeetingsFailure,
            navigate,
            loadCommitteeErrorCode,
            loadCommitteeMeetingsErrorCode,
        } = this.props;

        if (loadCommitteeFailure) {
            navigate(`/error/${loadCommitteeErrorCode}`);
        }
        if (loadCommitteeMeetingsFailure) {
            navigate(`/error/${loadCommitteeMeetingsErrorCode}`);
        }

        if (committeeId && committee?.name) {
            setDrawerItems(CommitteeSubMenu(committeeId, permissions));
            setBreadcrumbs(
                CommitteeMeetingsBreadcrumbs(committeeId, committee.name)
            );
        }

        if (committeeId && prevProps.committeeId != committeeId) {
            loadCommittee(committeeId);
        }
    }

    componentWillUnmount() {
        this.clearCommittee();
    }

    clearCommittee() {
        this.props.clearCommittee();
        this.props.clearCommitteeMeetings();
    }

    toggleExportMenu() {
        this.setState({
            exportToggle: !this.state.exportToggle,
        });
    }

    showTrials(): boolean {
        const { loadingCommitteeMeetings, loadCommitteeMeetingsSuccess } =
            this.props;

        if (!loadingCommitteeMeetings && loadCommitteeMeetingsSuccess) {
            return true;
        }

        return false;
    }

    render() {
        const {
            committeeId,
            committee,
            loadingCommittee,
            loadingCommitteeMeetings,
            permissions,
        } = this.props;

        return (
            <RestrictedLayout
                permission={Dtos.Permission.CommitteeMeetingView}
                subMenuTitle={committee ? committee.name : ""}
                subMenuItems={CommitteeSubMenu(committeeId, permissions)}
                loading={loadingCommittee || loadingCommitteeMeetings}
            >
                {this.renderContent()}
            </RestrictedLayout>
        );
    }

    renderContent() {
        const { permissions, committeeId } = this.props;

        const canCreate = AuthenticationHelper.hasPermission(
            permissions,
            Dtos.Permission.CommitteeMeetingCreate
        );

        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Meetings</h1>
                    </div>
                    <div className="col" style={{ textAlign: "right" }}>
                        {canCreate && (
                            <>
                                <Link
                                    className={"btn btn-primary"}
                                    url={`/committees/${committeeId}/meetings/create`}
                                >
                                    Create
                                </Link>
                            </>
                        )}
                    </div>
                </div>
                {!this.showTrials() ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {this.showTrials() ? (
                    <div className="card">
                        <div className="patient-tables">
                            {this.createTable()}
                        </div>
                    </div>
                ) : null}
            </div>
        );
    }

    createTable() {
        const { committeeId, committeeMeetings, pageSize, setPageSize } =
            this.props;

        if (!committeeMeetings || committeeMeetings.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>There are currently no meetings to view.</span>
                    </div>
                </div>
            );
        }

        const showPagination = committeeMeetings.length > pageSize;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={committeeMeetings}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination ? pageSize : committeeMeetings.length
                    }
                    pageSize={
                        showPagination ? pageSize : committeeMeetings.length
                    }
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no meetings to display"
                    resizable={false}
                    onPageSizeChange={(val) => setPageSize(val)}
                    getTdProps={(
                        state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (e, handleOriginal) => {
                                const url = `/committees/${committeeId}/meetings/${rowInfo.original.id}/details`;
                                this.props.navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    defaultSorted={[
                        {
                            id: "meetingDate",
                            desc: true,
                        },
                    ]}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Duotone.HANDSHAKE}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "meetingDate",
                            Header: "Date",
                            accessor: "meetingDate",
                            Cell: (props) => (
                                <span>
                                    {moment(
                                        props.value ??
                                            props?.original?.meetingDate,
                                        "YYYY-MM-DD HH:mm:ss.SSSSSSS Z"
                                    ).format("DD/MM/YYYY")}
                                </span>
                            ),
                        },
                        {
                            id: "city",
                            Header: "City",
                            accessor: "city",
                        },
                        {
                            id: "committeeMembers",
                            Header: "Comm. Members",
                            accessor: "committeeMembers",
                            Cell: (props) => (
                                <span>
                                    {
                                        committeeMeetings.find(
                                            (x) => x.id == props?.original?.id
                                        )?.committeeMembers?.length
                                    }
                                </span>
                            ),
                        },
                        {
                            id: "committeeRegularInvitee",
                            Header: "Invitees",
                            accessor: "committeeRegularInvitee",
                            Cell: (props) => (
                                <span>
                                    {
                                        committeeMeetings.find(
                                            (x) => x.id == props?.original?.id
                                        )?.committeeRegularInvitees?.length
                                    }
                                </span>
                            ),
                        },
                    ]}
                />
            </div>
        );
    }
}

const mapStateToProps = (
    state: ITmdState,
    ownProps: CommitteeMeetingsPageProps
): ICommitteeMeetingsPageProps => {
    let committee: Dtos.Committee | undefined = !(
        state.committees.formData instanceof Array
    )
        ? state.committees.formData
        : undefined;
    let committeeMeetings: Dtos.CommitteeMeeting[] | undefined =
        state.committeeMeeting.formData instanceof Array
            ? state.committeeMeeting.formData
            : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        history: ownProps.history,
        location: ownProps.location,
        match: ownProps.match,

        committeeMeetings: committeeMeetings,
        formFilter: state.trials.formFilter,
        lookups: state.committeeMeeting.lookups,

        loadingCommitteeMeetings:
            state.committeeMeeting.loadState &&
            state.committeeMeeting.loadState.status == RequestState.Pending,
        loadCommitteeMeetingsSuccess:
            state.committeeMeeting.loadState &&
            state.committeeMeeting.loadState.status == RequestState.Success,
        loadCommitteeMeetingsFailure:
            state.committeeMeeting.loadState &&
            state.committeeMeeting.loadState.status == RequestState.Failure,
        loadCommitteeMeetingsErrorCode:
            state.committeeMeeting.loadState.errorCode,

        committeeId:
            ownProps.match &&
            ownProps.match.params.committeeId.toString() != "create"
                ? ownProps.match.params.committeeId
                : undefined!,
        committee: committee!,

        loadingCommittee:
            state.committees.loadState &&
            state.committees.loadState.status == RequestState.Pending,
        loadCommitteeSuccess:
            state.committees.loadState &&
            state.committees.loadState.status == RequestState.Success,
        loadCommitteeFailure:
            state.committees.loadState &&
            state.committees.loadState.status == RequestState.Failure,
        loadCommitteeErrorCode: state.committees.loadState.errorCode,

        user: state.user.data,
        permissions: permissions,

        pageSize: state.pagingPage.pageSize,
    };
};

const mapDispatchToProps = (
    dispatch,
    ownProps: CommitteeMeetingsPageProps
): ICommitteeMeetingsPageActions => {
    return {
        navigate: ownProps.navigate,
        navigateReplace: ownProps.navigateReplace,

        loadCommitteeMeetings: bindActionCreators(
            CommitteeMeetingActions.LoadCommitteeMeetingByCommitteeId,
            dispatch
        ),
        clearCommitteeMeetings: bindActionCreators(
            CommitteeMeetingActions.Clear,
            dispatch
        ),

        loadCommittee: bindActionCreators(
            CommitteeActions.LoadCommitteeById,
            dispatch
        ),
        clearCommittee: bindActionCreators(CommitteeActions.Clear, dispatch),

        setPageSize: bindActionCreators(
            PagingActions.PageSizeChanged,
            dispatch
        ),

        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default RouterHoc<
    ICommitteeMeetingsPageParams,
    CommitteeMeetingsPageProps
>(
    connect(
        mapStateToProps,
        mapDispatchToProps
    )(
        CommitteeMeetingsPage
    ) as unknown as ComponentType<CommitteeMeetingsPageProps>
) as ComponentType<any>;
